import { motion } from "framer-motion";
import { XIcon } from "lucide-react";
import React, { useState } from "react";
import { toast } from "sonner";

import Spinner from "../../../../components/Spinner";
import { ResumeDataService } from "../../../../services/create-resume/resume-data";
import { Button } from "../../../JobsPage/components/Button";
import ApplicationConfirmation from "../../../JobsPage/components/jobApplication/ApplicationConfirmation";
import DocumentsConfirmation from "../../../JobsPage/components/jobApplication/DocumentsConfirmation";
import DSPSelection from "../../../JobsPage/components/jobApplication/DSPSelection";
// import ScreeningQuestions from "../../../JobsPage/components/jobApplication/ScreeningQuestions";
import { Modal } from "../../../Profile/components/Modal";
import ExternalApplicationRegModal from "./ExternalApplicationRegModal.jsx";
import Loader from "./Loader";

const initialApplicationState = {
	dsp: {
		dsp_id: null,
		dsp_title: "",
		dsp_docs: [],
	},
	screeningQuestions: [],
	emailTemplate: "",
};

const ApplyModal = ({ isOpen, onClose, jobTitle, companyName, applicationDetails, onDrawer = false }) => {
	const [stage, setStage] = useState(0);
	const [documentUrl, setDocumentUrl] = useState(null);
	const [loading, setLoading] = useState(false);
	const applicationService = new ResumeDataService();
	const [showDocument, setShowDocument] = useState(false);
	const [loadingDocument, setLoadingDocument] = useState(true);
	const [openExAppRegModal, setOpenExAppRegModal] = useState(false);
	const [smartApplication, setSmartApplication] = useState(initialApplicationState);

	const handleDSPChange = (data) => {
		const _smartApplication = { ...smartApplication };

		if (stage === 0 || stage === 1) _smartApplication.dsp = data;
		if (stage === 2) _smartApplication.screeningQuestions = data;

		setSmartApplication(_smartApplication);
	};

	const nextStage = async () => {
		if (stage < stages.length - 1) setStage(stage + 1);

		if (stage === stages.length - 1) {
			const applicationPayload = {
				jobId: applicationDetails.jobId,
				applicationEmail: applicationDetails.link,
				emailBody: "Hi, Am Applying for the supervisor post job. Regards, Shafii M.",
				dsp: {
					dsp_id: smartApplication.dsp.dsp_id,
					doc_ids: smartApplication.dsp.dsp_docs.map((doc) => doc?.doc_id),
				},
			};

			setLoading(true);

			await applicationService.submitOneClickApplication(
				applicationPayload,
				(response) => {
					setLoading(false);
					toast.success(response.data.message);
					onClose();
				},
				(err) => {
					setLoading(false);
					toast.error(`Error in processing application: ${err.response.data.message ?? err.response.data.error}`);
				},
			);

			return applicationPayload;
		}
	};

	const prevStage = (steps) => {
		if (stage > 0) setStage(stage - steps);
	};

	const stages = [
		{
			title: "Select your DSP",
			content: <DSPSelection smartApplication={smartApplication} onDSPChange={handleDSPChange} setStage={setStage} />,
		},
		{
			title: "Select documents to include in the DSP",
			content: (
				<DocumentsConfirmation
					smartApplication={smartApplication}
					onDSPChange={handleDSPChange}
					onPrev={() => {
						setShowDocument(false);
						setDocumentUrl(null);
						prevStage(1);
					}}
					setShowDocument={setShowDocument}
					setDocumentUrl={setDocumentUrl}
					setLoadingDocument={setLoadingDocument}
				/>
			),
		},
		// {
		// 	title: "Fill in the screening questions",
		// 	content: (
		// 		<ScreeningQuestions
		// 			smartApplication={smartApplication}
		// 			onDSPChange={handleDSPChange}
		// 			onPrev={() => prevStage(1)}
		// 		/>
		// 	),
		// },
		{
			title: "Preview and Apply",
			content: <ApplicationConfirmation smartApplication={smartApplication} onPrev={() => prevStage(2)} />,
		},
	];

	return (
		<>
			<ExternalApplicationRegModal
				open={openExAppRegModal}
				setOpen={setOpenExAppRegModal}
				applicationDetails={{ ...applicationDetails, companyName, jobTitle }}
			/>

			<Modal
				isOpen={isOpen}
				size={showDocument && documentUrl ? "xl" : "medium-large"}
				progress={applicationDetails.applicationType === "URL" ? undefined : { stages, stage }}
				stage={1}
				onClose={onClose}
				onDrawer={onDrawer && applicationDetails.applicationType !== "URL"}
				title={`Apply to ${jobTitle} at ${companyName}`}
				secondaryAction={
					applicationDetails.applicationType === "URL"
						? {
								label: "Continue",
								onClick: () => {
									window.open(applicationDetails.link, "_blank");
									setOpenExAppRegModal(true);
									onClose();
								},
							}
						: null
				}
			>
				<div className="overflow-y-auto">
					<div>
						<div className="pb-3 max-h-[calc(100vh-70px)] overflow-y-auto pr-2">
							<div className="flex flex-col md:flex-row gap-2">
								{showDocument && documentUrl && (
									<div className="w-full order-2 md:order-1">
										{loadingDocument && (
											<div className="h-[calc(100vh-110px)] flex flex-col items-center justify-center">
												<Loader />
											</div>
										)}
										{!loadingDocument && (
											<div className="flex justify-end mb-1">
												<Button
													variant="ghost"
													size="icon"
													onClick={() => {
														setDocumentUrl(null);
														setLoadingDocument(true);
														setShowDocument(false);
													}}
													className="text-gray-500 hover:text-gray-700 w-8 h-8 p-0 -mr-2 -mt-2 rounded-full"
												>
													<XIcon className="h-5 w-5" />
												</Button>
											</div>
										)}
										<iframe
											src={documentUrl}
											className="w-full h-[calc(100vh-110px)] rounded-lg border border-gray-100"
											title="Document Preview"
											onLoad={() => {
												setLoadingDocument(false);
											}}
										/>
									</div>
								)}
								<div className="w-full space-y-3 mb-3 order-1 md:order-2">
									{applicationDetails.applicationType === "URL" ? (
										<>
											<h3 className="text-[14px] font-medium font-inter text-gray-950">You&#39;re Leaving Dproz.</h3>
											<h3 className="text-[14px] font-medium font-inter text-gray-950">
												This Job application will take you to an external site. Ensure you follow the instructions on
												the site to complete your application. Your progress on Dproz will not be affected.
											</h3>
										</>
									) : (
										<>
											<div className="flex-grow flex items-center justify-center p-3">
												<motion.div
													key={stage}
													initial={{ opacity: 0, x: 50 }}
													animate={{ opacity: 1, x: 0 }}
													exit={{ opacity: 0, x: -50 }}
													transition={{ duration: 0.3 }}
													className="w-full"
												>
													{stages[stage].content}
												</motion.div>
											</div>
											{/* Navigation Buttons */}
											<div className="flex d-flex mt-4 mb-5 p-4 bg-white border-gray-200 bottom-0">
												<Button
													onClick={nextStage}
													disabled={
														stage === 0 && smartApplication.dsp.dsp_id === null
														// (stage === 2 && smartApplication.screeningQuestions.length === 0)
													}
													className="w-full"
												>
													{loading ? <Spinner /> : stage === stages.length - 1 ? "Submit Application" : "Next"}
												</Button>
											</div>
										</>
									)}
								</div>
							</div>
						</div>
					</div>
				</div>
			</Modal>
		</>
	);
};

export default ApplyModal;
